// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import { imageFormatFor } from "@/lib/agentcore-client"

/**
 * QUÉ CAMBIÓ, Y POR QUÉ ESTE ARCHIVO YA NO MIDE LO MISMO
 * ======================================================
 * Este módulo protegía el límite de cuerpo de **API Gateway (10,485,760 bytes)**,
 * medido contra el endpoint desplegado. La medición era correcta y el límite
 * existe, pero **no es el que muerde**: todo el tráfico entra por CloudFront, y el
 * WebACL de CloudFront tiene `AWSManagedRulesCommonRuleSet`, que incluye
 * `SizeRestrictions_BODY` — «Inspects for request bodies that are over 8 KB
 * (8,192 bytes). Rule action: Block».
 *
 * O sea que el guardrail vigilaba un techo **1,280 veces más alto** que el real.
 * Dejaba pasar cualquier imagen realista y CloudFront la rechazaba con un 403 de
 * HTML. Medido el 2026-08-18 contra el ambiente desplegado: un JPEG de 36 KB → un
 * cuerpo de ~48 KB → BLOCK por `SizeRestrictions_BODY`. En la práctica **ninguna**
 * imagen funcionaba, y el 403 no se parecía a un problema de tamaño.
 *
 * El arreglo real fue mover las imágenes al carril de S3 (ver el comentario del
 * carril en `ChatInterface`), con lo que los adjuntos dejaron de contar para el
 * cuerpo. Lo que queda por vigilar del lado del cliente son dos cosas distintas:
 *
 *   1. **El tamaño por archivo**, contra el techo que aplica el runtime. Antes se
 *      medía en agregado porque todo iba junto en el cuerpo; ahora cada archivo
 *      viaja por su cuenta a S3 y el agregado es irrelevante.
 *   2. **El texto del turno contra los 8 KB**, que es el único que todavía viaja en
 *      el cuerpo. Un promotor que pegue un brief largo puede pasarse, y sin este
 *      aviso recibiría el mismo 403 opaco.
 */

/**
 * Techo de `SizeRestrictions_BODY` en el WebACL de CloudFront. No es configurable
 * por regla: la única forma de permitir cuerpos mayores es poner esa regla en
 * `Count`, que es una decisión de postura de seguridad y no se tomó.
 *
 * Ojo con no confundirlo con el «body inspection size limit» del WebACL (16 KB por
 * defecto en CloudFront, subible a 64 KB): ése controla cuánto **inspecciona** WAF
 * para las reglas de XSS/SQLi, y subirlo NO levanta este bloqueo.
 */
export const WAF_MAX_BODY_BYTES = 8 * 1024

/**
 * Margen para todo lo que acompaña al prompt en el JSON: `session_id`, `modelId`,
 * `effort`, el arreglo `uploads` con sus keys, y las comillas y comas. Una key de
 * upload ronda los 120 bytes y caben 5 por turno, así que 2 KB cubre el sobre con
 * holgura y deja ~6 KB para el texto.
 */
const ENVELOPE_RESERVE_BYTES = 2 * 1024

/**
 * Techos por archivo que aplica el runtime en `_process_uploads`
 * (`promotores/app.py`). Son distintos por tipo porque Bedrock Converse los
 * distingue: 3.75 MB para imágenes, 4.5 MB para documentos. Se repiten acá para
 * poder avisar **antes** de subir a S3 en vez de dejar que el runtime lo rechace
 * después de que el usuario ya esperó la subida.
 */
export const MAX_IMAGE_BYTES = 3_750_000
export const MAX_DOCUMENT_BYTES = 4_500_000

/** Techo que le toca a un archivo según su carril. */
export function maxBytesFor(file: { type?: string; name?: string }): number {
  return imageFormatFor(file) !== undefined ? MAX_IMAGE_BYTES : MAX_DOCUMENT_BYTES
}

/**
 * Archivos que exceden el techo de su tipo, en el orden en que se adjuntaron.
 *
 * Devuelve los archivos y no un booleano para poder nombrarlos en el mensaje: con
 * cuatro adjuntos, «uno pesa demasiado» obliga al usuario a adivinar cuál.
 */
export function oversizedFiles<T extends { size: number; type?: string; name?: string }>(
  files: readonly T[]
): T[] {
  return files.filter(file => file.size > maxBytesFor(file))
}

export interface BodyBudget {
  /** Bytes que ocupará el cuerpo del turno, ya con el sobre. */
  bytes: number
  /** Presupuesto disponible para el cuerpo. */
  budgetBytes: number
  /** `true` si CloudFront lo bloquearía con `SizeRestrictions_BODY`. */
  exceeds: boolean
}

/**
 * Mide el cuerpo del turno contra el techo del WAF.
 *
 * Sólo cuenta el texto: los adjuntos viajan por S3 y en el cuerpo queda su
 * referencia, que ya está cubierta por `ENVELOPE_RESERVE_BYTES`.
 *
 * Se mide en **bytes UTF-8**, no en caracteres: el español acentuado y los emoji
 * ocupan 2–4 bytes cada uno, así que `prompt.length` subestimaría el cuerpo justo
 * en los textos que más se acercan al límite.
 */
export function bodyBudget(prompt: string): BodyBudget {
  const bytes = new TextEncoder().encode(prompt).length + ENVELOPE_RESERVE_BYTES
  return {
    bytes,
    budgetBytes: WAF_MAX_BODY_BYTES,
    exceeds: bytes > WAF_MAX_BODY_BYTES,
  }
}

/** Cuántos caracteres de más tiene el prompt, para poder pedir un recorte concreto. */
export function promptOverflowBytes(prompt: string): number {
  const { bytes, budgetBytes } = bodyBudget(prompt)
  return Math.max(0, bytes - budgetBytes)
}
